import { FunctionComponent, useCallback, useEffect, useRef } from 'react';
import { Navbar } from '../../../shared';
import { RootBox } from './styled';
import useAppDispatch from '../../../hooks/useAppDispatch';
import { useParams, useLocation } from 'react-router-dom';
import { isAdmin } from 'src/redux/auth/service';
import { useSelector } from 'react-redux';
import { getQuest } from '../../../redux/quests/service';
import {
    getQuest as getQuestSelector,
    getIsLoading as getIsLoadingSelector
} from '../../../redux/quests/selectors';
import { selectProject } from '../../../redux/client/service';
import Header from './components/Header';
import HeaderSkeleton from './components/Skeletons/HeaderSkeleton';
import DescriptionSection from './components/DescriptionSection';
import DescriptionSectionSkeleton from './components/Skeletons/DescriptionSectionSkeleton';
import QuestHistory from '../components/History';
import StatusSummarySection from './components/StatusSummarySection';
import QuestSolutionSection from './components/QuestSolutionSection';
import TasksSection from './components/TasksSection';
import PlanningSection from './components/PlanningSection';
import Spinner from 'src/shared/Spinner';
import { QuestStatus } from 'src/enums/quests';
import { useAsyncJobPolling } from 'src/hooks/useAsyncJobPolling';

const BREAKDOWN_POLL_INTERVAL_MS = 5000;

type Props = Record<string, never>;

type Params = {
    id: string;
};

interface LocationState {
    breakdownJobId?: string;
}

const QuestDetail: FunctionComponent<Props> = () => {
    const { id } = useParams<Params>();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const quest = useSelector(getQuestSelector);
    const isLoading = useSelector(getIsLoadingSelector);
    const jobIdRef = useRef<string | null>(
        (location.state as LocationState)?.breakdownJobId ?? null
    );

    useEffect(() => {
        if (!id) return;
        dispatch(getQuest({ questId: id }));
    }, [id, dispatch]);

    useEffect(() => {
        if (quest?.projectId) {
            dispatch(selectProject(String(quest.projectId)));
        }
    }, [quest?.projectId, dispatch]);

    const refreshQuest = useCallback(() => {
        if (id) {
            dispatch(getQuest({ questId: id }));
        }
    }, [dispatch, id]);

    const getJobId = useCallback(() => jobIdRef.current, []);

    useAsyncJobPolling({
        enabled: quest?.status === QuestStatus.NEW && !!id,
        intervalMs: BREAKDOWN_POLL_INTERVAL_MS,
        getJobId,
        onJobReady: refreshQuest,
        onRefresh: refreshQuest,
        onError: refreshQuest
    });

    return (
        <Navbar>
            <RootBox
                marginRight={2}
                sx={{ mx: { xs: 2, sm: 2, md: 40, lg: 40 }, my: 10 }}
            >
                {isLoading || !quest ? (
                    <>
                        <HeaderSkeleton />
                        <DescriptionSectionSkeleton />
                    </>
                ) : (
                    <>
                        <Header quest={quest} />
                        <PlanningSection quest={quest} />
                        <DescriptionSection quest={quest} />
                        {quest.statusSummary &&
                            Object.keys(quest.statusSummary).length > 0 && (
                                <StatusSummarySection quest={quest} />
                            )}
                        {isAdmin() && quest.tasks && quest.tasks.length > 0 && (
                            <TasksSection
                                tasks={quest.tasks}
                                activities={quest.activities ?? []}
                            />
                        )}
                        {quest.questSolutionPrs &&
                            quest.questSolutionPrs.length > 0 && (
                                <QuestSolutionSection quest={quest} />
                            )}
                        {quest.status === QuestStatus.NEW && (
                            <Spinner text="We are preparing your request" />
                        )}
                        <QuestHistory questId={quest.id} />
                    </>
                )}
            </RootBox>
        </Navbar>
    );
};

export default QuestDetail;
